import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Menu } from '@headlessui/react';
import { Plus, Smartphone, Square, Trash2, ChevronDown, MoreVertical, Pencil, Copy, Download, Move, RectangleHorizontal, RectangleVertical } from 'lucide-react';

const RightSidebar = ({
    selectedClip,
    selectedLayerId,
    onUpdateClip,
    exportRatios = [],
    onToggleExportRatio,
    previewAspectRatio,
    originalAspectRatio = '9:16',
    onPreviewAspectRatioChange,
    activeCropFormat,
    onToggleCropMode,
    // Variation props
    variations = [],
    activeVariationId,
    onSwitchVariation,
    onSaveAsNew,
    onRenameVariation,
    onDeleteVariation,
    onDuplicateVariation,
    onDownloadVariation,
    isSavingVariation = false,
    deletingVariationId = null,
    selectedForExport = new Set(),
    onToggleExportSelection,
}) => {
    // Rename state
    const [editingVariationId, setEditingVariationId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const editInputRef = useRef(null);

    useEffect(() => {
        if (editingVariationId && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [editingVariationId]);

    const getVariationId = (v) => v.id || v._id;

    const handleStartRename = (variation) => {
        setEditingVariationId(getVariationId(variation));
        setEditingName(variation.title || variation.name || '');
    };

    const handleFinishRename = () => {
        if (editingVariationId && editingName.trim() && onRenameVariation) {
            onRenameVariation(editingVariationId, editingName.trim());
        }
        setEditingVariationId(null);
        setEditingName('');
    };

    const handleDeleteVariation = (variationId) => {
        if (onDeleteVariation) onDeleteVariation(variationId);
    };

    const handleDuplicateVariation = (variation) => {
        if (onDuplicateVariation) {
            onDuplicateVariation(variation);
        } else if (onSaveAsNew) {
            onSaveAsNew();
        }
    };

    const getIconForFormat = (format) => {
        if (format === '16:9') return <RectangleHorizontal className="h-5 w-5" />;
        if (format === '1:1') return <Square className="h-5 w-5" />;
        return <RectangleVertical className="h-5 w-5" />;
    };

    // Build the variations list: "Original" + saved variations
    const allVariations = [
        { id: '__original__', name: 'Original', title: 'Original', format: originalAspectRatio, isOriginal: true },
        ...variations.map(v => ({
            ...v,
            id: getVariationId(v),
            name: v.title || v.name || 'Untitled',
            format: v.editorProgress?.format || v.editorProgress?.previewRatio || v.format || '9:16',
            isOriginal: false,
        }))
    ];

    const currentActiveId = activeVariationId === null ? '__original__' : activeVariationId;

    const AVAILABLE_FORMATS = [
        { name: '9:16', label: 'Vertical (9:16)' },
        { name: '4:5', label: 'Portrait (4:5)' },
        { name: '2:3', label: 'Portrait (2:3)' },
        { name: '1:1', label: 'Square (1:1)' },
        { name: '16:9', label: 'Wide (16:9)' },
    ];

    // Portal-based menu positioning for the 3-dot actions
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [, setActiveMenuId] = useState(null);
    const menuBtnRefs = useRef({});

    const updateMenuPosition = useCallback((variationId) => {
        const btnEl = menuBtnRefs.current[variationId];
        if (btnEl) {
            const rect = btnEl.getBoundingClientRect();
            setMenuPosition({
                top: rect.bottom + 4,
                left: rect.right - 176, // w-44 = 11rem = 176px, align right edge
            });
        }
        setActiveMenuId(variationId);
    }, []);

    return (
        <div className="w-72 h-full bg-[#F9FAFB] dark:bg-[#111111] border-l border-[#E5E7EB] dark:border-white/10 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Reframe / Variations Section */}
                <div>
                    <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Reframe</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                        Create additional aspect-ratio versions of your video by adjusting the framing for each one.
                    </p>

                    <div className="space-y-2">
                        {allVariations.map((variation) => {
                            const isActive = currentActiveId === variation.id;
                            const isEditing = editingVariationId === variation.id;
                            const isDeleting = deletingVariationId === variation.id;
                            const isActiveCrop = activeCropFormat === variation.format;

                            return (
                                <div key={variation.id} className="relative group">
                                    <button
                                        onClick={() => {
                                            if (variation.isOriginal) {
                                                onSwitchVariation(null);
                                            } else {
                                                onSwitchVariation(variation.id);
                                            }
                                        }}
                                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg w-full min-w-0 overflow-hidden text-left transition-colors ${isActive
                                            ? 'bg-orange-500/10 dark:bg-orange-500/20 border border-orange-500/40'
                                            : 'hover:bg-gray-50 dark:hover:bg-white/5 border border-transparent'
                                            }`}
                                    >
                                        {/* Export selection checkbox */}
                                        {variations.length > 0 && (
                                            <input
                                                type="checkbox"
                                                checked={selectedForExport.has(variation.isOriginal ? 'original' : variation.id)}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    onToggleExportSelection && onToggleExportSelection(variation.isOriginal ? 'original' : variation.id);
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                className={`w-3.5 h-3.5 rounded accent-orange-500 cursor-pointer flex-shrink-0 ${selectedForExport.has(variation.isOriginal ? 'original' : variation.id)
                                                    ? ''
                                                    : 'opacity-0 group-hover:opacity-100'
                                                    }`}
                                            />
                                        )}

                                        {/* Aspect ratio icon */}
                                        <div className={`flex-shrink-0 w-5 flex items-center justify-center ${isActive ? 'text-orange-500' : 'text-gray-600 dark:text-gray-400'
                                            }`}>
                                            {getIconForFormat(variation.format)}
                                        </div>

                                        {/* Format ratio */}
                                        <span className={`font-semibold text-sm flex-shrink-0 w-10 text-left ${isActive ? 'text-orange-500' : 'text-gray-900 dark:text-white'
                                            }`}>
                                            {variation.format || '9:16'}
                                        </span>

                                        {/* Editable name */}
                                        {isEditing ? (
                                            <input
                                                ref={editInputRef}
                                                type="text"
                                                value={editingName}
                                                onChange={(e) => setEditingName(e.target.value)}
                                                onBlur={handleFinishRename}
                                                onClick={(e) => e.stopPropagation()}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleFinishRename();
                                                    if (e.key === 'Escape') {
                                                        setEditingVariationId(null);
                                                        setEditingName('');
                                                    }
                                                }}
                                                className="ml-auto text-sm font-medium border border-orange-400 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-orange-500 w-28 bg-white dark:bg-black text-gray-900 dark:text-white"
                                            />
                                        ) : (
                                            <span
                                                className={`ml-auto text-xs flex-1 min-w-0 -ml-2 ${variation.isOriginal ? '' : 'cursor-text'
                                                    } ${isActive ? 'text-orange-500/70' : 'text-gray-400 dark:text-gray-500'
                                                    }`}
                                                onDoubleClick={(e) => {
                                                    if (variation.isOriginal) return;
                                                    e.stopPropagation();
                                                    handleStartRename(variation);
                                                }}
                                                title={variation.isOriginal ? variation.name : `${variation.name}`}
                                            >
                                                {variation.name?.length > 12 ? `${variation.name.substring(0, 12)}...` : variation.name}
                                            </span>
                                        )}

                                        {/* Reposition + More actions (not for original, not while editing) */}
                                        {!isEditing && (!variation.isOriginal || variation.format === '9:16') && (
                                            <div className={`flex items-center gap-0.5 flex-shrink-0 transition-opacity ${isActive && isActiveCrop ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                                                {/* Reposition / Crop icon (Not for 9:16) */}
                                                {variation.format !== '9:16' && (
                                                    <div
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (onToggleCropMode) onToggleCropMode(isActiveCrop ? null : variation.format);
                                                            if (!isActive) {
                                                                onSwitchVariation(variation.id);
                                                            }
                                                        }}
                                                        className={`h-6 w-6 flex items-center justify-center rounded transition-all ${isActiveCrop
                                                            ? 'text-white bg-orange-500 opacity-100'
                                                            : 'text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-white/10'
                                                            }`}
                                                        title="Adjust Crop Position"
                                                    >
                                                        <Move className="h-3.5 w-3.5" />
                                                    </div>
                                                )}

                                                {/* More options dropdown */}
                                                <Menu as="div" className="relative">
                                                    <Menu.Button
                                                        ref={(el) => { if (el) menuBtnRefs.current[variation.id] = el; }}
                                                        className="h-6 w-6 flex items-center justify-center rounded hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                                                        onClick={(e) => { e.stopPropagation(); updateMenuPosition(variation.id); }}
                                                    >
                                                        {isDeleting ? (
                                                            <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin text-gray-500" />
                                                        ) : (
                                                            <MoreVertical className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                                                        )}
                                                    </Menu.Button>
                                                    {createPortal(
                                                        <Menu.Items
                                                            className="fixed w-44 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-white/10 rounded-xl shadow-2xl z-[9999] py-1 focus:outline-none"
                                                            style={{ top: menuPosition.top, left: menuPosition.left }}
                                                        >
                                                            {!variation.isOriginal && (
                                                                <Menu.Item>
                                                                    {({ active }) => (
                                                                        <button
                                                                            onClick={(e) => { e.stopPropagation(); handleStartRename(variation); }}
                                                                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${active ? 'bg-gray-100 dark:bg-white/5' : ''
                                                                                } text-gray-700 dark:text-gray-300 cursor-pointer`}
                                                                        >
                                                                            <Pencil className="h-4 w-4" />
                                                                            Rename
                                                                        </button>
                                                                    )}
                                                                </Menu.Item>
                                                            )}
                                                            <Menu.Item>
                                                                {({ active }) => (
                                                                    <button
                                                                        onClick={(e) => { e.stopPropagation(); handleDuplicateVariation(variation); }}
                                                                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${active ? 'bg-gray-100 dark:bg-white/5' : ''
                                                                            } text-gray-700 dark:text-gray-300 cursor-pointer`}
                                                                    >
                                                                        <Copy className="h-4 w-4" />
                                                                        Duplicate
                                                                    </button>
                                                                )}
                                                            </Menu.Item>
                                                            <Menu.Item>
                                                                {({ active }) => (
                                                                    <button
                                                                        onClick={(e) => { e.stopPropagation(); if (onDownloadVariation) onDownloadVariation(variation.isOriginal ? '__original__' : variation.id); }}
                                                                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${active ? 'bg-gray-100 dark:bg-white/5' : ''
                                                                            } text-gray-700 dark:text-gray-300 cursor-pointer`}
                                                                    >
                                                                        <Download className="h-4 w-4" />
                                                                        Download
                                                                    </button>
                                                                )}
                                                            </Menu.Item>
                                                            {!variation.isOriginal && (
                                                                <>
                                                                    <div className="border-t border-gray-200 dark:border-white/10 my-1" />
                                                                    <Menu.Item>
                                                                        {({ active }) => (
                                                                            <button
                                                                                onClick={(e) => { e.stopPropagation(); handleDeleteVariation(variation.id); }}
                                                                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${active ? 'bg-red-50 dark:bg-red-500/10' : ''
                                                                                    } text-red-600 cursor-pointer`}
                                                                            >
                                                                                <Trash2 className="h-4 w-4" />
                                                                                Delete
                                                                            </button>
                                                                        )}
                                                                    </Menu.Item>
                                                                </>
                                                            )}
                                                        </Menu.Items>,
                                                        document.body
                                                    )}
                                                </Menu>
                                            </div>
                                        )}
                                    </button>
                                </div>
                            );
                        })}

                        {/* Add new format dropdown */}
                        <Menu as="div" className="relative">
                            {({ open }) => (
                                <>
                                    <Menu.Button className="w-full flex items-center justify-between gap-3 h-10 px-3 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 rounded-lg transition-colors">
                                        <div className="flex items-center gap-3">
                                            <Plus className="h-4 w-4" />
                                            <span className="font-medium text-sm">Add new format</span>
                                        </div>
                                        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                                    </Menu.Button>
                                    <Menu.Items className="absolute left-0 right-0 bottom-full mb-1 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-white/10 rounded-xl shadow-2xl z-[9999] py-1 focus:outline-none">
                                        {AVAILABLE_FORMATS.map((format) => (
                                            <Menu.Item key={format.name}>
                                                {({ active }) => (
                                                    <button
                                                        onClick={() => {
                                                            if (onSaveAsNew) onSaveAsNew(format.name);
                                                        }}
                                                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm ${active ? 'bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-400'
                                                            } cursor-pointer`}
                                                    >
                                                        <span className="text-gray-400">
                                                            <Smartphone className="h-4 w-4" />
                                                        </span>
                                                        <span className="font-semibold">{format.name}</span>
                                                        <span className="ml-auto text-xs text-gray-400">{format.label}</span>
                                                    </button>
                                                )}
                                            </Menu.Item>
                                        ))}
                                    </Menu.Items>
                                </>
                            )}
                        </Menu>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RightSidebar;
